import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import apiService from "../../services/api";
import SolicitarConfirm from "./SolicitarConfirm";
import { usePopup } from "../common/Popup";
import ComboBox from "../common/ComboBox/ComboBox";
import { CONSELHOS_PROFISSIONAIS } from "../../constants/conselhosProfissionais";
import {
  CONSELHO_OUTRO,
  montarConselhoClasse,
  validarConselhoClasse,
} from "../../utils/conselhoClasse";
import { cpf as validarCpf, required } from "../../utils/validation";
import { formatCPF } from "../../utils/formatters";

const FormContainer = styled.div`
  padding: 8px;
  max-width: 800px;
  margin-left: 60px;
  position: relative;
`;

const FormTitle = styled.h2`
  font-size: 16px;
  color: #ff9f1f;
  font-weight: 600;
  margin-bottom: 12px;
`;

const Section = styled.div`
  margin-bottom: 16px;
`;

const FormGroup = styled.div`
  margin-bottom: 10px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  color: #002e3b;
  margin-bottom: 5px;
  font-weight: bold;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  font-size: 14px;
  border: 1px solid ${(props) => (props.$hasError ? "#d00" : "#ccc")};
  border-radius: 10px;
  outline: none;

  &:focus {
    border-color: ${(props) => (props.$hasError ? "#d00" : "#6ab8a8")};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  font-size: 14px;
  border: 1px solid ${(props) => (props.$hasError ? "#d00" : "#ccc")};
  border-radius: 10px;
  outline: none;
  background-color: white;
  color: #002e3b;

  option {
    background-color: white;
    color: #002e3b;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  font-size: 14px;
  border: 1px solid ${(props) => (props.$hasError ? "#d00" : "#ccc")};
  border-radius: 10px;
  outline: none;
  resize: vertical;
  min-height: 80px;
`;

const FieldError = styled.div`
  color: #d00;
  font-size: 11px;
  margin-top: 4px;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const Column = styled.div`
  flex: ${(props) => (props.flex ? props.flex : 1)};
  min-width: 200px;
`;

const LinkButton = styled.button`
  display: block;
  background: none;
  border: none;
  padding: 0;
  margin-top: 6px;
  color: #00a574;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #008a61;
  }
`;

const DaysContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
`;

const DayLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #002e3b;

  input {
    width: 16px;
    height: 16px;
  }
`;

const UploadSectionTitle = styled.div`
  font-size: 13px;
  color: #00a574;
  font-weight: 600;
  margin-bottom: 4px;
`;

const UploadHint = styled.div`
  font-size: 12px;
  color: #002e3b;
  margin-bottom: 12px;
`;

const UploadContainer = styled.div`
  border: 1px dashed #c8c8c8;
  border-radius: 12px;
  padding: 16px 12px 20px;
  text-align: center;
  background-color: #fffef9;
`;

const UploadText = styled.div`
  font-size: 12px;
  color: #7a7a7a;
  margin-bottom: 16px;
`;

const FileInput = styled.input`
  display: none;
`;

const UploadButton = styled.label`
  background-color: #ff9f1f;
  color: #ffffff;
  font-size: 15px;
  font-weight: 500;
  padding: 10px 36px;
  border-radius: 9px;
  cursor: pointer;
  display: inline-block;
  box-shadow: 0px 5px 3px 0px rgba(0, 0, 0, 0.15);
`;

const NextButton = styled.button`
  padding: 8px 20px;
  background-color: #6ab8a8;
  color: #ffffff;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  font-size: 15px;
  min-width: 140px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.223);
  align-self: flex-end;

  &:hover {
    background-color: #56a998ff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  margin-bottom: 64px;
`;

const VoltarButton = styled.button`
  background-color: #999;
  color: #ffffff;
  border: none;
  padding: 8px 24px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: 0px 4px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;

  &:hover {
    background-color: #888;
  }
`;

const OPCOES_CONSELHO = CONSELHOS_PROFISSIONAIS.map((conselho) => ({
  value: conselho.value,
  label: conselho.sigla,
  descricao: conselho.descricao,
  busca: conselho.busca,
}));

const LIMITES_HORARIO_POR_TURNO = {
  Matutino: { min: "07:00", max: "12:00", descricao: "07:00 às 12:00" },
  Vespertino: { min: "13:00", max: "19:00", descricao: "13:00 às 19:00" },
  Noturno: { min: "19:00", max: "07:00", descricao: "19:00 às 07:00" },
};

const horarioDentroDoTurno = (horario, turno) => {
  if (!horario) return false;

  const limite = LIMITES_HORARIO_POR_TURNO[turno];
  if (!limite) return false;

  if (turno === "Noturno") {
    return horario >= limite.min || horario <= limite.max;
  }

  return horario >= limite.min && horario <= limite.max;
};

const minutosNoTurno = (horario, turno) => {
  const [horas, minutos] = horario.split(":").map(Number);
  const total = horas * 60 + minutos;

  return turno === "Noturno" && horario <= "07:00" ? total + 24 * 60 : total;
};

const REQUIRED_FIELD_NAMES = [
  "curso",
  "disciplina",
  "fase",
  "qtdAlunos",
  "horasPorAluno",
  "dataInicio",
  "dataFim",
  "horaInicio",
  "horaFim",
  "docenteResponsavel",
  "cpfDocenteResponsavel",
  "conselho",
  "diasSemana",
  "objetivo",
];

const SolicitarForm = ({ user, vaga, onVoltar }) => {
  const navigate = useNavigate();
  const popup = usePopup();
  const [cursos, setCursos] = useState([]);
  const [selectedCursoId, setSelectedCursoId] = useState("");
  const [diasSemanaSelecionados, setDiasSemanaSelecionados] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [planoEnsinoFile, setPlanoEnsinoFile] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [usouModoManual, setUsouModoManual] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [formData, setFormData] = useState({
    tipoAtividade: "",
    coordCursoNome: "",
    coordCursoEmail: "",
    coordEstagioNome: "",
    coordEstagioEmail: "",
    setor: "",
    disciplina: "",
    fase: "",
    qtdAlunos: "",
    horasPorAluno: "",
    dataInicio: "",
    dataFim: "",
    turno: "",
    horaInicio: "",
    horaFim: "",
    docenteResponsavel: "",
    cpfDocenteResponsavel: "",
    conselhoValue: "",
    conselhoOutro: "",
    conselhoRegistro: "",
    objetivo: "",
  });

  const cargoId = user?.cargoId;

  useEffect(() => {
    if (!vaga) return;

    setFormData((prev) => ({
      ...prev,
      tipoAtividade: vaga.tipoAtividade || "",
      setor: vaga.unidadeAssistencial?.nome || "",
      turno: vaga.turno || "",
    }));
  }, [vaga]);

  useEffect(() => {
    const loadCursos = async () => {
      try {
        const data = await apiService.getMyCursos();
        const lista = Array.isArray(data) ? data : data?.data || [];
        setCursos(lista);

        if (lista.length > 0 && cargoId === 3) {
          const curso = lista[0];
          setSelectedCursoId(String(curso.id));
          setFormData((prev) => ({
            ...prev,
            coordCursoNome: curso.coordenadorCurso || "",
            coordCursoEmail: curso.emailCoordenadorCurso || "",
            coordEstagioNome: curso.coordenadorEstagio || "",
            coordEstagioEmail: curso.emailCoordenadorEstagio || "",
          }));
        }
      } catch (error) {
        console.error("Erro ao carregar cursos:", error);
      }
    };

    if (user) {
      loadCursos();
    }
  }, [user, cargoId]);

  const handleChange = (e) => {
    const { name } = e.target;
    const value = name === "cpfDocenteResponsavel" ? formatCPF(e.target.value) : e.target.value;

    if (name === "setor" && (cargoId === 2 || cargoId === 3)) {
      return;
    }


    // Validação: quantidade de alunos não pode ser maior que vagas disponíveis
    if (name === 'qtdAlunos' && vaga?.quantidadeDisponivel) {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue > vaga.quantidadeDisponivel) {
        popup.alert({
          icon: "warning",
          message: `A quantidade de alunos não pode ser maior que o número de vagas disponíveis (${vaga.quantidadeDisponivel}).`,
        });
        return;
      }
    }
    
    if (name === "dataInicio") {
      setFormData((prev) => ({
        ...prev,
        dataInicio: value,
        dataFim: prev.dataFim && prev.dataFim < value ? "" : prev.dataFim,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleConselhoChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      conselhoValue: value,
      conselhoOutro: value === CONSELHO_OUTRO ? prev.conselhoOutro : "",
    }));

    if (touched.conselho) {
      const novoConselhoOutro = value === CONSELHO_OUTRO ? formData.conselhoOutro : "";
      const erro = validarConselhoClasse({
        value,
        outro: novoConselhoOutro,
        registro: formData.conselhoRegistro,
      });
      setFieldErrors((prev) => ({ ...prev, conselho: erro }));
    }
  };

  const entrarModoManual = () => {
    setUsouModoManual(true);
    handleConselhoChange(CONSELHO_OUTRO);
  };

  const handleCursoChange = (e) => {
    const value = e.target.value;
    setSelectedCursoId(value);

    const curso = cursos.find((c) => String(c.id) === value);
    if (curso) {
      setFormData((prev) => ({
        ...prev,
        coordCursoNome: curso.coordenadorCurso || "",
        coordCursoEmail: curso.emailCoordenadorCurso || "",
        coordEstagioNome: curso.coordenadorEstagio || "",
        coordEstagioEmail: curso.emailCoordenadorEstagio || "",
      }));
    }
  };

  const handleDiaSemanaChange = (e) => {
    const { value, checked } = e.target;
    const atualizado = checked
      ? [...diasSemanaSelecionados, value]
      : diasSemanaSelecionados.filter((d) => d !== value);

    setTouched((prev) => ({ ...prev, diasSemana: true }));
    setDiasSemanaSelecionados(atualizado);
    setFieldErrors((prev) => ({
      ...prev,
      diasSemana: atualizado.length === 0 ? "Selecione pelo menos um dia da semana" : "",
    }));
  };

  const handlePlanoEnsinoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setPlanoEnsinoFile(file);
    } else {
      setPlanoEnsinoFile(null);
    }
  };

  const validateField = (name) => {
    switch (name) {
      case "curso":
        return required(selectedCursoId);
      case "disciplina":
        return required(formData.disciplina);
      case "fase":
        return required(formData.fase);
      case "qtdAlunos": {
        const erroObrigatorio = required(formData.qtdAlunos);
        if (erroObrigatorio) return erroObrigatorio;
        if (Number(formData.qtdAlunos) < 1) return "Informe ao menos 1 aluno";
        if (vaga?.quantidadeDisponivel && Number(formData.qtdAlunos) > vaga.quantidadeDisponivel) {
          return `Máximo de ${vaga.quantidadeDisponivel} vagas disponíveis`;
        }
        return "";
      }
      case "horasPorAluno": {
        const erroObrigatorio = required(formData.horasPorAluno);
        if (erroObrigatorio) return erroObrigatorio;
        return Number(formData.horasPorAluno) < 1 ? "Informe um valor maior que zero" : "";
      }
      case "dataInicio":
        return required(formData.dataInicio);
      case "dataFim": {
        const erroObrigatorio = required(formData.dataFim);
        if (erroObrigatorio) return erroObrigatorio;
        if (formData.dataInicio && formData.dataFim <= formData.dataInicio) {
          return "Deve ser posterior à data de início";
        }
        return "";
      }
      case "horaInicio": {
        const erroObrigatorio = required(formData.horaInicio);
        if (erroObrigatorio) return erroObrigatorio;
        const limite = LIMITES_HORARIO_POR_TURNO[formData.turno];
        if (!horarioDentroDoTurno(formData.horaInicio, formData.turno)) {
          return limite
            ? `Informe um horário no intervalo de ${limite.descricao}`
            : "Turno inválido para seleção do horário";
        }
        return "";
      }
      case "horaFim": {
        const erroObrigatorio = required(formData.horaFim);
        if (erroObrigatorio) return erroObrigatorio;
        const limite = LIMITES_HORARIO_POR_TURNO[formData.turno];
        if (!horarioDentroDoTurno(formData.horaFim, formData.turno)) {
          return limite
            ? `Informe um horário no intervalo de ${limite.descricao}`
            : "Turno inválido para seleção do horário";
        }
        if (
          formData.horaInicio &&
          horarioDentroDoTurno(formData.horaInicio, formData.turno) &&
          minutosNoTurno(formData.horaFim, formData.turno) <=
            minutosNoTurno(formData.horaInicio, formData.turno)
        ) {
          return "Deve ser posterior ao horário de início";
        }
        return "";
      }
      case "docenteResponsavel":
        return required(formData.docenteResponsavel);
      case "cpfDocenteResponsavel": {
        const erroObrigatorio = required(formData.cpfDocenteResponsavel);
        if (erroObrigatorio) return erroObrigatorio;
        return validarCpf(formData.cpfDocenteResponsavel);
      }
      case "conselho":
        return validarConselhoClasse({
          value: formData.conselhoValue,
          outro: formData.conselhoOutro,
          registro: formData.conselhoRegistro,
        });
      case "diasSemana":
        return diasSemanaSelecionados.length === 0 ? "Selecione pelo menos um dia da semana" : "";
      case "objetivo":
        return required(formData.objetivo);
      default:
        return "";
    }
  };

  const validateAll = () => {
    const errors = {};
    let hasErrors = false;
    for (const name of REQUIRED_FIELD_NAMES) {
      const error = validateField(name);
      if (error) {
        errors[name] = error;
        hasErrors = true;
      }
    }
    return { errors, hasErrors };
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setFieldErrors((prev) => ({ ...prev, [name]: validateField(name) }));
  };

  const getError = (name) => (touched[name] ? fieldErrors[name] : "");

  const handleConselhoBlur = () => {
    setTouched((prev) => ({ ...prev, conselho: true }));
    setFieldErrors((prev) => ({ ...prev, conselho: validateField("conselho") }));
  };

  const validateBeforeSubmit = () => {
    if (!vaga?.id) {
      popup.alert({
        icon: "warning",
        message: "Vaga inválida. Volte e selecione novamente.",
      });
      return false;
    }

    const allTouched = REQUIRED_FIELD_NAMES.reduce((acc, name) => ({ ...acc, [name]: true }), {});
    setTouched(allTouched);

    const { errors, hasErrors } = validateAll();
    setFieldErrors(errors);

    return !hasErrors;
  };

  const handleNext = (e) => {
    e.preventDefault();

    if (!validateBeforeSubmit()) {
      return;
    }
    setShowConfirm(true);
  };

  const handleSubmit = async () => {
    if (!validateBeforeSubmit()) {
      setShowConfirm(false);
      return;
    }

    try {
      setSubmitting(true);

      let planoEnsinoId;
      if (planoEnsinoFile) {
        try {
          const uploadResponse = await apiService.uploadPlanoEnsino(planoEnsinoFile);
          planoEnsinoId = uploadResponse.arquivoId;
        } catch (error) {
          console.error("Erro ao enviar plano de ensino:", error);
          await popup.alert({
            icon: "warning",
            title: "Erro ao enviar plano de ensino",
            message: error.message || "Tente novamente.",
          });
          setSubmitting(false);
          return;
        }
      }

      const dataInicioISO = new Date(formData.dataInicio + "T00:00:00").toISOString();
      const dataFimISO = new Date(formData.dataFim + "T23:59:59").toISOString();

      const payload = {
        vagaId: vaga.id,
        cursoId: Number(selectedCursoId),
        nomeDisciplina: formData.disciplina.trim(),
        fase: formData.fase,
        docenteResponsavel: formData.docenteResponsavel,
        cpfDocenteResponsavel: formData.cpfDocenteResponsavel.replace(/\D/g, ""),
        conselhoClasse: montarConselhoClasse({
          value: formData.conselhoValue,
          outro: formData.conselhoOutro,
          registro: formData.conselhoRegistro,
        }),
        quantidadeAlunos: Number(formData.qtdAlunos || 0),
        horasTotaisPorAluno: Number(formData.horasPorAluno || 0),
        horaInicio: formData.horaInicio,
        horaFim: formData.horaFim,
        objetivoEstagio: formData.objetivo,
        planoEnsinoId: planoEnsinoId || undefined,
        diasSemana: diasSemanaSelecionados,
        periodos: [
          {
            dataInicio: dataInicioISO,
            dataFim: dataFimISO,
          },
        ],
      };

      console.log("Enviando solicitação de vaga:", payload);
      const response = await apiService.createSolicitacaoVaga(payload);
      console.log("Solicitação criada com sucesso:", response);
      await popup.alert({
        icon: "success",
        message:
          "Solicitação registrada com sucesso! A partir de agora, ela seguirá para análise e concessão pelo hospital.",
      });

      navigate("/VagasSolicitadas");

      setFormData({
        tipoAtividade: "",
        coordCursoNome: "",
        coordCursoEmail: "",
        coordEstagioNome: "",
        coordEstagioEmail: "",
        setor: "",
        disciplina: "",
        fase: "",
        qtdAlunos: "",
        horasPorAluno: "",
        dataInicio: "",
        dataFim: "",
        turno: "",
        horaInicio: "",
        horaFim: "",
        docenteResponsavel: "",
        cpfDocenteResponsavel: "",
        conselhoValue: "",
        conselhoOutro: "",
        conselhoRegistro: "",
        objetivo: "",
      });
      setDiasSemanaSelecionados([]);
      setPlanoEnsinoFile(null);
      setShowConfirm(false);
    } catch (error) {
      console.error("Erro ao criar solicitação de vaga:", error);
      await popup.alert({
        icon: "warning",
        title: "Erro ao registrar solicitação",
        message: error.message || "Verifique os dados e tente novamente.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (showConfirm) {
    const cursoSelecionado = cursos.find(
      (curso) => String(curso.id) === String(selectedCursoId)
    );

    return (
      <SolicitarConfirm
        data={{
          ...formData,
          conselhoClasse: montarConselhoClasse({
            value: formData.conselhoValue,
            outro: formData.conselhoOutro,
            registro: formData.conselhoRegistro,
          }),
        }}
        cursoNome={
          cursoSelecionado
            ? cursoSelecionado.curso || cursoSelecionado.nome
            : ""
        }
        diasSemana={diasSemanaSelecionados}
        planoEnsinoFileName={planoEnsinoFile?.name}
        onBack={() => setShowConfirm(false)}
        onSubmit={handleSubmit}
        submitting={submitting}
      />
    );
  }

  const limiteHorario = LIMITES_HORARIO_POR_TURNO[formData.turno];

  return (
    <FormContainer>
      <FormTitle>
        Preencha os dados para fazer a solicitação de estágio
        <br />
        <span style={{ fontSize: 12, color: "#002E3B", fontWeight: "normal" }}>
          Favor confirmar os dados dos representantes legais.
        </span>
      </FormTitle>

      <form onSubmit={handleNext}>
        <Section>
          <Row>
            <Column flex={1}>
              <FormGroup>
                <Label>Tipo de atividade*</Label>
                <Select
                  name="tipoAtividade"
                  value={formData.tipoAtividade}
                  onChange={handleChange}
                  disabled
                  style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                >
                  <option value="">Selecione uma opção</option>
                  <option value="ESTAGIO_SUPERVISIONADO">
                    Estágio Supervisionado
                  </option>
                  <option value="ESTAGIO_OBRIGATORIO">
                    Estágio Obrigatório
                  </option>
                  <option value="INTERNATO_MEDICO">Internato Médico</option>
                  <option value="AULA_PRATICA">Aula Prática</option>
                </Select>
              </FormGroup>
            </Column>
            <Column flex={1}>
              <FormGroup>
                <Label>Curso*</Label>
                <Select
                  name="curso"
                  value={selectedCursoId}
                  onChange={handleCursoChange}
                  onBlur={handleBlur}
                  disabled={cargoId === 3}
                  $hasError={!!getError("curso")}
                >
                  <option value="">Selecione um curso</option>
                  {cursos.map((curso) => (
                    <option key={curso.id} value={curso.id}>
                      {curso.curso || curso.nome}
                    </option>
                  ))}
                </Select>
                {getError("curso") && <FieldError>{getError("curso")}</FieldError>}
              </FormGroup>
            </Column>
          </Row>

          <Row>
            <Column flex={1}>
              <FormGroup>
                <Label>Coordenador do curso*</Label>
                <Input
                  name="coordCursoNome"
                  value={formData.coordCursoNome}
                  onChange={handleChange}
                  disabled
                  style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                />
              </FormGroup>
            </Column>
            <Column flex={1}>
              <FormGroup>
                <Label>E-mail do coordenador do curso*</Label>
                <Input
                  type="email"
                  name="coordCursoEmail"
                  value={formData.coordCursoEmail}
                  onChange={handleChange}
                  disabled
                  style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                />
              </FormGroup>
            </Column>
          </Row>

          <Row>
            <Column flex={1}>
              <FormGroup>
                <Label>Coordenador de estágio*</Label>
                <Input
                  name="coordEstagioNome"
                  value={formData.coordEstagioNome}
                  onChange={handleChange}
                  disabled
                  style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                />
              </FormGroup>
            </Column>
            <Column flex={1}>
              <FormGroup>
                <Label>E-mail do coordenador de estágio*</Label>
                <Input
                  type="email"
                  name="coordEstagioEmail"
                  value={formData.coordEstagioEmail}
                  onChange={handleChange}
                  disabled
                  style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                />
              </FormGroup>
            </Column>
          </Row>
        </Section>

        <Section>
          <Row>
            <Column flex={1}>
              <FormGroup>
                <Label>Setor*</Label>
                <Input
                  name="setor"
                  value={formData.setor}
                  onChange={handleChange}
                  disabled={cargoId === 2 || cargoId === 3}
                  style={
                    cargoId === 2 || cargoId === 3
                      ? { backgroundColor: "#f5f5f5", cursor: "not-allowed" }
                      : undefined
                  }
                />
              </FormGroup>
            </Column>
            <Column flex={1.5}>
              <FormGroup>
                <Label>Nome da disciplina*</Label>
                <Input
                  name="disciplina"
                  value={formData.disciplina}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  $hasError={!!getError("disciplina")}
                />
                {getError("disciplina") && <FieldError>{getError("disciplina")}</FieldError>}
              </FormGroup>
            </Column>
            <Column flex={0.7}>
              <FormGroup>
                <Label>Fase*</Label>
                <Select
                  name="fase"
                  value={formData.fase}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  $hasError={!!getError("fase")}
                >
                  <option value="">Selecione a fase</option>
                  {Array.from({ length: 12 }, (_, index) => index + 1).map(
                    (faseNumero) => (
                      <option key={faseNumero} value={String(faseNumero)}>
                        {`${faseNumero}ª`}
                      </option>
                    ),
                  )}
                </Select>
                {getError("fase") && <FieldError>{getError("fase")}</FieldError>}
              </FormGroup>
            </Column>
          </Row>

          <Row>
            <Column flex={1}>
              <FormGroup>
                <Label>Quantidade de alunos*</Label>
                <Input
                  type="number"
                  name="qtdAlunos"
                  min="1"
                  max={vaga?.quantidadeDisponivel || undefined}
                  value={formData.qtdAlunos}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  $hasError={!!getError("qtdAlunos")}
                />
                {vaga?.quantidadeDisponivel && (
                  <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                    Máximo: {vaga.quantidadeDisponivel} vagas disponíveis
                  </div>
                )}
                {getError("qtdAlunos") && <FieldError>{getError("qtdAlunos")}</FieldError>}
              </FormGroup>
            </Column>
            <Column flex={1}>
              <FormGroup>
                <Label>Nº total de horas p/ aluno*</Label>
                <Input
                  type="number"
                  name="horasPorAluno"
                  min="1"
                  value={formData.horasPorAluno}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  $hasError={!!getError("horasPorAluno")}
                />
                {getError("horasPorAluno") && <FieldError>{getError("horasPorAluno")}</FieldError>}
              </FormGroup>
            </Column>
          </Row>

          <Row>
            <Column flex={1}>
              <FormGroup>
                <Label>Data de início do estágio*</Label>
                <Input
                  type="date"
                  name="dataInicio"
                  value={formData.dataInicio}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  $hasError={!!getError("dataInicio")}
                />
                {getError("dataInicio") && <FieldError>{getError("dataInicio")}</FieldError>}
              </FormGroup>
            </Column>
            <Column flex={1}>
              <FormGroup>
                <Label>Data de fim do estágio*</Label>
                <Input
                  type="date"
                  name="dataFim"
                  value={formData.dataFim}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  min={formData.dataInicio || undefined}
                  $hasError={!!getError("dataFim")}
                />
                {getError("dataFim") && <FieldError>{getError("dataFim")}</FieldError>}
              </FormGroup>
            </Column>
          </Row>

          <Row>
            <Column flex={1}>
              <FormGroup>
                <Label>Turno*</Label>
                <Select
                  name="turno"
                  value={formData.turno}
                  onChange={handleChange}
                  disabled
                  style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                >
                  <option value="">Selecione uma opção</option>
                  <option value="Matutino">Matutino</option>
                  <option value="Vespertino">Vespertino</option>
                  <option value="Noturno">Noturno</option>
                </Select>
              </FormGroup>
            </Column>
            <Column flex={1}>
              <FormGroup>
                <Label>Horário Início*</Label>
                <Input
                  type="time"
                  name="horaInicio"
                  value={formData.horaInicio}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  min={limiteHorario?.min}
                  max={limiteHorario?.max}
                  required
                  $hasError={!!getError("horaInicio")}
                />
                {getError("horaInicio") && (
                  <FieldError>{getError("horaInicio")}</FieldError>
                )}
              </FormGroup>
            </Column>
            <Column flex={1}>
              <FormGroup>
                <Label>Horário Término*</Label>
                <Input
                  type="time"
                  name="horaFim"
                  value={formData.horaFim}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  min={limiteHorario?.min}
                  max={limiteHorario?.max}
                  required
                  $hasError={!!getError("horaFim")}
                />
                {getError("horaFim") && (
                  <FieldError>{getError("horaFim")}</FieldError>
                )}
              </FormGroup>
            </Column>
          </Row>

          <FormGroup>
            <Label>Dias da semana</Label>
            <DaysContainer>
              {[
                "Segunda",
                "Terça",
                "Quarta",
                "Quinta",
                "Sexta",
                "Sábado",
                "Domingo",
              ].map((dia) => (
                <DayLabel key={dia}>
                  <input
                    type="checkbox"
                    name="diasSemana"
                    value={dia}
                    checked={diasSemanaSelecionados.includes(dia)}
                    onChange={handleDiaSemanaChange}
                  />
                  {dia}
                </DayLabel>
              ))}
            </DaysContainer>
            {getError("diasSemana") && <FieldError>{getError("diasSemana")}</FieldError>}
          </FormGroup>

          <Row>
            <Column flex={1}>
              <FormGroup>
                <Label>Docente responsável*</Label>
                <Input
                  name="docenteResponsavel"
                  value={formData.docenteResponsavel}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  $hasError={!!getError("docenteResponsavel")}
                />
                {getError("docenteResponsavel") && <FieldError>{getError("docenteResponsavel")}</FieldError>}
              </FormGroup>
            </Column>
            <Column flex={1}>
              <FormGroup>
                <Label>CPF do docente responsável*</Label>
                <Input
                  name="cpfDocenteResponsavel"
                  value={formData.cpfDocenteResponsavel}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="000.000.000-00"
                  maxLength={14}
                  $hasError={!!getError("cpfDocenteResponsavel")}
                />
                {getError("cpfDocenteResponsavel") && <FieldError>{getError("cpfDocenteResponsavel")}</FieldError>}
              </FormGroup>
            </Column>
          </Row>

          <Row>
            <Column flex={1}>
              <FormGroup>
                <Label>Conselho de classe*</Label>
                {formData.conselhoValue === CONSELHO_OUTRO ? (
                  <>
                    <Input
                      name="conselhoOutro"
                      value={formData.conselhoOutro}
                      onChange={handleChange}
                      onBlur={handleConselhoBlur}
                      placeholder="Digite a sigla do conselho (ex: CRBM-2)"
                      maxLength={60}
                      $hasError={!!getError("conselho")}
                    />
                    <LinkButton
                      type="button"
                      onClick={() => handleConselhoChange("")}
                    >
                      Escolher um conselho da lista
                    </LinkButton>
                  </>
                ) : (
                  <>
                    <ComboBox
                      options={OPCOES_CONSELHO}
                      value={formData.conselhoValue}
                      onChange={handleConselhoChange}
                      placeholder="Busque por sigla, região ou estado"
                      acaoVazia={
                        usouModoManual ? null : (
                          <LinkButton type="button" onClick={entrarModoManual}>
                            Informar conselho manualmente
                          </LinkButton>
                        )
                      }
                    />
                    {usouModoManual && (
                      <LinkButton type="button" onClick={entrarModoManual}>
                        Informar conselho manualmente
                      </LinkButton>
                    )}
                  </>
                )}
                {getError("conselho") && <FieldError>{getError("conselho")}</FieldError>}
              </FormGroup>
            </Column>
            <Column flex={1}>
              <FormGroup>
                <Label>Registro profissional*</Label>
                <Input
                  name="conselhoRegistro"
                  value={formData.conselhoRegistro}
                  onChange={handleChange}
                  onBlur={handleConselhoBlur}
                  disabled={!formData.conselhoValue}
                  placeholder="Ex: 12345 ou 123456-F"
                  maxLength={20}
                  $hasError={!!getError("conselho")}
                />
              </FormGroup>
            </Column>
          </Row>

          <FormGroup>
            <Label>Objetivo do estágio*</Label>
            <TextArea
              name="objetivo"
              value={formData.objetivo}
              onChange={handleChange}
              onBlur={handleBlur}
              $hasError={!!getError("objetivo")}
            />
            {getError("objetivo") && <FieldError>{getError("objetivo")}</FieldError>}
          </FormGroup>
        </Section>

        <Section>
          <UploadSectionTitle>Anexe o Plano de Ensino</UploadSectionTitle>
          <UploadHint>
            Adicione o documento com a seguinte nomenclatura:
            <br />
            <b>planoDeEnsino - nomedadisciplina-fase.pdf</b>
          </UploadHint>

          <UploadContainer>
            <UploadText>
              Arraste o seu arquivo até aqui
              <br />
              ou selecione um arquivo do seu computador
            </UploadText>

            {planoEnsinoFile && (
              <div
                style={{
                  fontSize: 12,
                  color: "#002E3B",
                  marginBottom: 8,
                  wordBreak: "break-all",
                }}
              >
                Arquivo selecionado: <b>{planoEnsinoFile.name}</b>
              </div>
            )}

            <FileInput
              id="planoEnsinoFile"
              type="file"
              accept="application/pdf"
              onChange={handlePlanoEnsinoChange}
            />
            <UploadButton htmlFor="planoEnsinoFile">
              {submitting ? "Enviando..." : "Selecionar"}
            </UploadButton>
          </UploadContainer>
          <ActionsRow>
            {onVoltar ? (
              <VoltarButton type="button" onClick={onVoltar} disabled={submitting}>
                Voltar
              </VoltarButton>
            ) : (
              <span />
            )}
            <NextButton type="submit">
              <span>Próximo</span>
              <span style={{ marginLeft: 8 }}>{">"}</span>
            </NextButton>
          </ActionsRow>
        </Section>
      </form>
    </FormContainer>
  );
};

export default SolicitarForm;
